import type { ReactNode } from 'react';
import { useState } from 'react';
import { cn } from 'utils/cn';

interface DisclosureListProps<T> {
    items: T[];
    maxVisibleItems: number;
    renderVisible: (visibleItems: T[]) => ReactNode;
    moreLabel: (hiddenCount: number) => ReactNode;
    lessLabel: ReactNode;
    className?: string;
}

/**
 * Shows the first `maxVisibleItems` items behind a focusable "+n more" / "show less" toggle.
 * `renderVisible` gets the whole visible slice, not one item at a time, so callers that join items
 * with separators or re-sort them keep that behavior.
 */
export function DisclosureList<T>({
    items,
    maxVisibleItems,
    renderVisible,
    moreLabel,
    lessLabel,
    className,
}: DisclosureListProps<T>): ReactNode {
    const [expanded, setExpanded] = useState(false);
    const hasMore = items.length > maxVisibleItems;
    const visible = expanded ? items : items.slice(0, maxVisibleItems);

    return (
        <div className={cn('flex gap-1', className)}>
            {renderVisible(visible)}
            {hasMore && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    className="text-primary w-fit cursor-pointer text-xs underline"
                >
                    {expanded ? lessLabel : moreLabel(items.length - maxVisibleItems)}
                </button>
            )}
        </div>
    );
}
